// ProfileSwitcher — header chip that lets a parent toggle between their
// children's profiles after logging in. Hidden when user has no family.

import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Users, Check, ChevronDown } from 'lucide-react';

const fullName = (m) => {
  if (!m) return '';
  if (m.first_name && m.last_name) return `${m.first_name} ${m.last_name}`;
  return m.first_name || m.name || 'Profil';
};

const initials = (m) => {
  const fn = m?.first_name || m?.name || '?';
  return fn.trim().charAt(0).toUpperCase();
};

const ProfileSwitcher = ({ compact = false }) => {
  const { family, activeProfile, activeProfileId, switchProfile } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  // Hide switcher when user isn't in a family or only has one profile.
  if (!family || !Array.isArray(family.members) || family.members.length < 2) return null;

  return (
    <div className="relative" ref={ref} data-testid="profile-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-2 rounded-full border border-[#EDE8DC] bg-white hover:border-[#D4A574] transition-colors ${
          compact ? 'px-2 py-1' : 'px-3 py-1.5'
        }`}
        data-testid="profile-switcher-trigger"
      >
        <span className="w-6 h-6 rounded-full bg-gradient-to-br from-[#D4A574] to-[#5D4E37] text-white text-xs font-semibold flex items-center justify-center">
          {initials(activeProfile)}
        </span>
        <span className="text-sm font-medium text-[#5D4E37] max-w-[120px] truncate">
          {fullName(activeProfile)}
        </span>
        <ChevronDown className="w-3.5 h-3.5 text-[#5D4E37]/60" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-64 bg-white border border-[#EDE8DC] rounded-sm shadow-lg z-50 overflow-hidden"
          data-testid="profile-switcher-menu"
        >
          <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-[#5D4E37]/50 bg-[#FAF6EC] border-b border-[#EDE8DC] flex items-center gap-1.5">
            <Users className="w-3 h-3" />
            {family.name || 'Famille'}
          </div>
          {family.members.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => { switchProfile(m.id); setOpen(false); }}
              className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors ${
                m.id === activeProfileId
                  ? 'bg-[#FAF6EC] text-[#5D4E37]'
                  : 'hover:bg-[#FAF6EC]/50 text-[#5D4E37]'
              }`}
              data-testid={`profile-switcher-option-${m.id}`}
            >
              <span className="w-6 h-6 rounded-full bg-gradient-to-br from-[#D4A574] to-[#5D4E37] text-white text-xs font-semibold flex items-center justify-center">
                {initials(m)}
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-medium truncate">{fullName(m)}</span>
                {m.is_family_parent && (
                  <span className="block text-[10px] uppercase tracking-widest text-[#D4A574]">Espace élève</span>
                )}
              </span>
              {m.id === activeProfileId && <Check className="w-4 h-4 text-[#D4A574]" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProfileSwitcher;
